// Pure presentation maths, lifted out of the render frame loop so it can be unit-tested
// without a canvas: snapshot interpolation and the walk-cycle gait state machine. Both
// are functions of their inputs (the snapshots, the per-figure gait state, dt) — no DOM,
// no service — so scripts/client-sim.js can exercise them directly.

import { strideLength, gaitBlend } from "../../../content/figure.js";

// Interpolate entity positions/angles between the previous and current snapshot by
// `alpha` (0..1, how far this frame is between the two 30Hz frames). Entities only in
// `cur` (just spawned) draw at their current spot; with no previous frame, or no current,
// there's nothing to blend.
export function interpolate(prev, cur, alpha) {
    if (!cur) return [];
    if (!prev) return cur.ents;
    const pmap = new Map(prev.ents.map((e) => [e.id, e]));
    return cur.ents.map((e) => {
        const p = pmap.get(e.id);
        if (!p) return e;
        return { ...e, x: p.x + (e.x - p.x) * alpha, y: p.y + (e.y - p.y) * alpha, a: p.a + (e.a - p.a) * alpha };
    });
}

// SMOOTH ZOOM (Set 15, generalised in Set 16). The engine owns only the SMOOTHING: a zoom PLUGIN says
// where it wants the camera (client.lua's `set_zoom(f)`), and this walks there.
//
// `t` is a plain LINEAR 0..1 position that moves toward the target's position, a full sweep taking
// `secs`. The ease is applied to the VALUE, not baked into the position — which is what makes changing
// your mind partway do the right thing: the position reverses from wherever it got to and retraces the
// identical curve, at the speed that curve has there. Easing the position instead would jump.
//
// That one property is why hold-to-zoom and step-to-zoom can be two plugins over the same code: one
// moves the target between two values, the other between five, and neither thinks about animation.
// Returns the new position and the zoom factor for it (1 = resting, `max` = fully pulled back).
export const ZOOM = { max: 2.4, secs: 0.3 };

// The cubic in/out ease, and its INVERSE. The inverse is the load-bearing one: a plugin names a zoom
// FACTOR, and to walk there we need the position whose EASED value is that factor. Inverting only the
// linear part would leave set_zoom(1.35) settling at 1.09 — the target you asked for would never
// arrive, and every intermediate step of a stepped zoom would land somewhere else than it claimed.
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeInv = (e) => (e < 0.5 ? Math.cbrt(e / 4) : 1 - Math.cbrt(2 * (1 - e)) / 2);

// Where on the 0..1 curve a zoom FACTOR sits, so a plugin can name a factor and we can walk to it.
const zoomPos = (f, tuning) => easeInv(Math.max(0, Math.min(1, ((f || 1) - 1) / (tuning.max - 1))));

// `target` is the zoom factor a plugin asked for (1 = resting). A boolean is still accepted as the
// old hold-or-not form, so nothing that predates set_zoom has to change.
export function advanceZoom(t, dt, target, tuning = ZOOM) {
    const want = typeof target === "boolean" ? (target ? 1 : 0) : zoomPos(target, tuning);
    const step = dt / tuning.secs;
    const next = want > t ? Math.min(want, t + step) : Math.max(want, t - step);
    return { t: next, zoom: 1 + (tuning.max - 1) * ease(next) };
}

// The walk-cycle smoothing (see the long comment at the gait section of the render loop).
export const GAIT = {
    smooth: 0.08,   // s — time constant for smoothing interpolated speed
    ease: 0.09,     // s — how long the legs take to ease into/out of the walk
    minSpeed: 20,   // px/s below which the figure is standing, not walking
};

// Advance one stick figure's gait by how far it actually moved this frame. Mutates `gt`
// ({phase, x, speed, walk, gait}) and writes the pose fields onto `e`. `e.x` is
// INTERPOLATED between snapshots, so the per-frame dx is noisy — hence the EMA on speed
// and the eased 0..1 `walk`/`gait` blends rather than a switch on a knife edge. Phase
// advances on ACTUAL distance over the current gait's stride, so the planted foot grips
// the ground at any speed or size (strideLength/gaitBlend from figure.js).
export function advanceGait(gt, e, dt, tuning = GAIT) {
    const dx = e.x - gt.x;
    gt.x = e.x;

    const speed = dt > 0 ? Math.abs(dx) / dt : 0;                       // px/s this frame
    gt.speed += (speed - gt.speed) * Math.min(1, dt / tuning.smooth);   // EMA — kills snapshot noise
    const target = gt.speed > tuning.minSpeed ? 1 : 0;
    gt.walk += (target - gt.walk) * Math.min(1, dt / tuning.ease);      // ease into/out of the cycle

    const wantGait = gaitBlend(gt.speed, e.r, e.sc);                    // walk↔run, blended on speed
    gt.gait += (wantGait - gt.gait) * Math.min(1, dt / tuning.ease);

    gt.phase = (gt.phase + Math.abs(dx) / strideLength(e.r, e.sc, gt.gait)) % 1;
    e.gaitPhase = gt.phase;
    e.walk = gt.walk;
    e.gait = gt.gait;
}

// A fresh gait state for a figure first seen at x.
export const newGait = (x) => ({ phase: 0, x, speed: 0, walk: 0, gait: 0 });
